import {useCallback, useEffect, useState} from 'react';
import type {ControlState, EligibilityReason} from './controlState';
import {deriveControlState} from './controlState';
import {
	getAccountLimitNotice,
	getAvailableAgents,
	getRunState,
	hasAgentToRun,
	invoke,
	isAtRunCap,
	markRead,
	stop,
	subscribeToAnyRunStateChange,
	subscribeToAvailableAgents,
	subscribeToRunState,
} from './novaInvocationStore';
import type {InvokeResult} from './novaInvocationStore';

export interface NovaInvocationStateResult {
	state: ControlState;
	accountLimitNotice: 'near_limit' | 'at_limit' | null;
	onAction: () => void;
	onOpenOutput: () => void;
	onStop: () => void;
}

const IDLE_RUN_STATE = {hasEverRun: false, isRunning: false, outputRead: false, outcome: null};

export function useNovaInvocationState(
	taskId: string,
	eligibility: EligibilityReason | null,
	// The caller (ConnectedNovaInvocationControl) must still call this hook unconditionally on
	// every render regardless of the feature flag — Rules of Hooks forbids skipping a hook call
	// itself. Passing `enabled` in lets the flag-off case skip the store subscriptions and their
	// per-render-and-per-event cost internally instead, since its result is discarded either way.
	enabled = true,
	// Threaded straight through to invoke() for the notification it may raise on completion — this
	// hook has no other way to know a task's display name, and re-deriving it from taskId alone
	// isn't possible.
	taskName?: string,
	// Same reasoning as taskName — lets the notification link to the task from anywhere, not just
	// the page the run started on.
	companyProjectId?: string | number,
	companyTaskId?: string | number,
	// For the notification's project-code pill — same reasoning as companyProjectId/companyTaskId.
	projectColor?: string,
	// Whether the task already has a conversation on the server (Task.hasNovaConversation). Passed
	// in rather than read from the store: it is a property of the task as the server knows it, not
	// of anything this session did, and the store only ever knows what this tab has done.
	hasConversation?: boolean,
	// Called with what the run came to. The control learns everything else through its store
	// subscription; this is for what the store cannot show, such as a refusal that saved no run.
	onSettled?: (result: InvokeResult) => void
): NovaInvocationStateResult {
	const [runState, setRunState] = useState(() => (enabled ? getRunState(taskId) : IDLE_RUN_STATE));
	const [atRunCap, setAtRunCap] = useState(() => enabled && isAtRunCap());
	const [accountLimitNotice, setAccountLimitNotice] = useState(() => (enabled ? getAccountLimitNotice() : null));
	// The agent list is one shared, per-session thing written once by useNovaAvailableAgents, so this
	// is read from the store and subscribed to rather than passed in: the control that renders first
	// is not the one whose fetch resolves, and every mounted control has to pick the answer up.
	// Without the subscription the first-load window would latch — a control that mounted before the
	// list resolved would stay disabled until something else happened to re-render it.
	const [hasAgent, setHasAgent] = useState(() => !enabled || hasAgentToRun());

	useEffect(() => {
		if (!enabled) return undefined;
		return subscribeToRunState(taskId, () => setRunState(getRunState(taskId)));
	}, [taskId, enabled]);
	// Both are properties of the whole tracked set, not of this one task — another task starting
	// or finishing must be able to flip this idle control's disabled state and its account-limit
	// banner too. atRunCap only flips exactly at the cap boundary, so accountLimitNotice needs its
	// own read here too — otherwise an idle control never re-renders (and so never picks up the
	// notice) for the near_limit case, which sits below that boundary.
	useEffect(() => {
		if (!enabled) return undefined;
		return subscribeToAnyRunStateChange(() => {
			setAtRunCap(isAtRunCap());
			setAccountLimitNotice(getAccountLimitNotice());
		});
	}, [enabled]);
	useEffect(() => {
		if (!enabled) return undefined;
		// Read once on subscribe as well as on every dispatch: the list can resolve between this
		// component's first render and this effect running, and that write dispatches to nobody yet.
		setHasAgent(hasAgentToRun());
		return subscribeToAvailableAgents(() => setHasAgent(hasAgentToRun()));
	}, [enabled]);

	const state = deriveControlState({...runState, eligibility, atRunCap, hasAgent, hasConversation});

	// There is no account-default agent yet (that decision is still open), so a bare Action click
	// — one that never went through the popover — falls back to whichever agent happens to be
	// first in the resolved list, rather than leaving the run agent-less. The control is disabled
	// whenever that list is empty (deriveControlState's no_agent_available branch), so this reads a
	// resolved list rather than racing one.
	//
	// A user picking an agent from the popover calls novaInvocationStore.invoke(taskId, agentId)
	// directly (see NovaAgentPickerForTask) — a separate call site, not through this hook — and that
	// explicit choice is never overridden here.
	// The run promise invoke() hands back is deliberately dropped rather than returned: this is a
	// click handler, the control learns the result through its store subscription, and a handler that
	// returned a thenable would change what its callers see — React's own act() among them.
	const onAction = useCallback((): void => {
		const run = invoke(taskId, getAvailableAgents()[0]?.id, {taskName, companyProjectId, companyTaskId, projectColor});
		if (onSettled) run.then(onSettled);
	}, [taskId, taskName, companyProjectId, companyTaskId, projectColor, onSettled]);
	const onOpenOutput = useCallback(() => markRead(taskId), [taskId]);
	const onStop = useCallback(() => stop(taskId), [taskId]);

	return {state, accountLimitNotice, onAction, onOpenOutput, onStop};
}
